import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Stack,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Divider,
} from '@mui/material';
import Iconify from '../iconify';
import * as adminOrgService from '../../services/adminOrganizationService';
import { errorNotification, infoNotification } from '../../services/notificationService';

export default function ManageMembersDialog({ open, onClose, organization }) {
  const [orgDetails, setOrgDetails] = useState(null);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');

  const loadData = useCallback(async () => {
    if (!organization?.id) return;
    try {
      const [details, inviteList] = await Promise.all([
        adminOrgService.getOrganization(organization.id),
        adminOrgService.listInvites(organization.id),
      ]);
      setOrgDetails(details);
      setInvites(inviteList);
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to load organization data');
    }
  }, [organization?.id]);

  useEffect(() => {
    if (open) {
      loadData();
    }
  }, [open, loadData]);

  const handleSendInvite = async () => {
    if (!inviteEmail) return;
    try {
      await adminOrgService.createInvite(organization.id, inviteEmail, inviteRole);
      infoNotification('Invite sent successfully');
      setInviteEmail('');
      setInviteRole('member');
      loadData();
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to send invite');
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    try {
      await adminOrgService.revokeInvite(organization.id, inviteId);
      infoNotification('Invite revoked');
      loadData();
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to revoke invite');
    }
  };

  const handleChangeRole = async (userId, role) => {
    try {
      await adminOrgService.changeMemberRole(organization.id, userId, role);
      infoNotification('Role updated');
      loadData();
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to change role');
    }
  };

  const handleRemoveMember = async (userId) => {
    try {
      await adminOrgService.removeMember(organization.id, userId);
      infoNotification('Member removed');
      loadData();
    } catch (error) {
      errorNotification(error.response?.data?.error || 'Failed to remove member');
    }
  };

  const formatExpiry = (dateStr) => {
    if (!dateStr) return '\u2014';
    return new Date(dateStr).toLocaleDateString();
  };

  const members = orgDetails?.users || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Manage Members — {organization?.name}</DialogTitle>
      <DialogContent>
        {/* Send Invite Section */}
        <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>
          Send Invite
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <TextField
            label="Email"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            size="small"
            sx={{ flex: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={inviteRole}
              label="Role"
              onChange={(e) => setInviteRole(e.target.value)}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="viewer">Viewer</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" onClick={handleSendInvite} disabled={!inviteEmail}>
            Send
          </Button>
        </Stack>

        {/* Pending Invites Section */}
        {invites.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Pending Invites
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invites.map((invite) => (
                    <TableRow key={invite.id}>
                      <TableCell>{invite.email}</TableCell>
                      <TableCell>{invite.role}</TableCell>
                      <TableCell>{formatExpiry(invite.expiresAt)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRevokeInvite(invite.id)}
                        >
                          <Iconify icon="eva:close-circle-outline" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Members Section */}
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Members
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell>{member.username}</TableCell>
                  <TableCell>{member.email}</TableCell>
                  <TableCell>
                    {member.role === 'owner' ? (
                      <Chip label="Owner" size="small" color="primary" />
                    ) : (
                      <Select
                        value={member.role}
                        size="small"
                        onChange={(e) => handleChangeRole(member.id, e.target.value)}
                        sx={{ minWidth: 100 }}
                      >
                        <MenuItem value="member">Member</MenuItem>
                        <MenuItem value="viewer">Viewer</MenuItem>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {member.role !== 'owner' && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveMember(member.id)}
                      >
                        <Iconify icon="eva:person-remove-outline" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {members.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      No members
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
}
